import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Briefcase, 
  Calendar, 
  MapPin, 
  Code, 
  CheckCircle2, 
  ChevronRight,
  Sparkles,
  Building2,
  Terminal
} from 'lucide-react';
import styled from 'styled-components';

const ExperienceCard = ({ exp, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6, 
        delay: index * 0.15,
        ease: [0.215, 0.61, 0.355, 1] 
      }
    },
    exit: {
      opacity: 0,
      y: -30,
      transition: {
        duration: 0.4,
        ease: "easeIn"
      }
    }
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      exit="exit"
      viewport={{ once: false, margin: "-50px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="experience-item"
    >

      <div className="experience-card">
        {/* Experience Header */}
        <div className="card-header">
          <div className="title-section">
            <h3 className="position-title">
              <Briefcase className="icon" />
              {exp.position}
            </h3>
            <div className="company-info">
              <span className="company-name">
                <Building2 className="icon" />
                {exp.company}
              </span>
              <span className="separator" />
              <span className="location">
                <MapPin className="icon" />
                {exp.location}
              </span>
              <span className="separator" />
              <span className="duration">
                <Calendar className="icon" />
                {exp.duration}
              </span>
            </div>
          </div>
          
          <div className="type-badge">
            <span className="badge">
              {exp.type}
            </span>
          </div>
        </div>

        {/* Overview */}
        <div className="overview-section">
         
          <p className="overview-text">
            "{exp.overview}"
          </p>
        </div>

        {/* Technologies Grid */}
        <div className="tech-section">
          <div className="section-header">
            <Terminal className="icon" />
            <h4>Core Stack</h4>
          </div>
          <div className="tech-grid">
            {exp.technologies.map((tech, idx) => (
              <motion.span
                key={idx}
                whileHover={{ scale: 1.05, y: -2 }}
                className="tech-tag"
              >
                {tech}
              </motion.span>
            ))}
          </div>
        </div>

        {/* Responsibilities */}
        <div className="responsibilities-section">
          <div className="section-header">
            <CheckCircle2 className="icon" />
            <h4>Key Impact & Roles</h4>
          </div>
          <ul className="responsibilities-list">
            {exp.responsibilities.map((resp, idx) => (
              <motion.li 
                key={idx}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + (idx * 0.05) }}
                className="responsibility-item"
              >
                <ChevronRight className="chevron-icon" />
                <span>{resp}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

const Experience = () => {
  const experiences = [
    {
      id: 1,
      position: "Senior Full Stack Developer",
      company: "Flitz Interactive",
      location: "Ahmedabad",
      duration: "Aug 2025 – Present",
      type: "Full-time",
      overview: "Working on multi-tenant internal business applications, focusing on secure, scalable, and maintainable full-stack solutions.",
      technologies: ["React", "Next.js", "Node.js", "Express.js", "MongoDB", "SQL", "Redux Toolkit", "PHP", "Laravel", "JWT", "bcrypt", "Multer", "Swiper"],
      responsibilities: [
        "Developed multi-tenant applications supporting multiple organizations with tenant-aware data isolation.",
        "Implemented tenant-aware RBAC (role-based access control), operational dashboards, and REST APIs.",
        "Built end-to-end features, including frontend components, backend logic, and database integration.",
        "Optimized application performance, code scalability, and maintainability across multiple modules.",
        "Conducted unit testing, debugging, and code reviews to ensure high-quality deliverables.",
        "Worked closely with product teams to define requirements, design solutions, and deploy features.",
        "Created reusable components and templates to accelerate development across multiple internal applications."
      ]
    },
    {
      id: 2,
      position: "Associate Software Development Engineer",
      company: "Block Stars",
      location: "South Korea (Remote)",
      duration: "Jul 2023 – Apr 2025",
      type: "Full-time",
      overview: "Developed large-scale slot gaming platforms, handling both frontend and backend systems for multiple vendor integrations.",
      technologies: ["HTML", "CSS", "JavaScript", "React", "Redux Toolkit", "Next.js", "Node.js", "Express.js", "MongoDB", "Mongoose", "SQL", "PHP", "Laravel", "JWT", "bcrypt", "Multer", "Swiper"],
      responsibilities: [
        "Designed and implemented end-to-end full-stack features for slot gaming projects.",
        "Integrated multiple third-party vendor game modules, including functionality, aggregation, and scoring systems.",
        "Built automation scripts for cloning and deploying popular vendor games, reducing manual deployment time.",
        "Implemented multi-layer authentication & authorization, including JWT-based login and role management.",
        "Optimized API performance, database queries, and caching mechanisms for high-load systems.",
        "Collaborated with cross-functional teams including QA, designers, and product managers to deliver features on time.",
        "Developed reusable frontend components using React and Swiper for dynamic game UIs.",
        "Gained experience in multi-vendor integrations, aggregation logic, and backend orchestration."
      ]
    },
    {
      id: 3,
      position: "Full Stack Trainee",
      company: "Chaincode Consulting LLP",
      location: "Chandigarh",
      duration: "Jan 2023 – Apr 2023",
      type: "Full-time",
      overview: "Worked on a proof-of-concept NFT platform, gaining hands-on experience in full-stack web development, blockchain integration basics, and project lifecycle management.",
      technologies: ["HTML", "CSS", "JavaScript", "React", "Redux", "Next.js", "Node.js", "Express.js", "MongoDB", "Mongoose", "SQL", "Multer", "JWT", "bcrypt", "Swiper"],
      responsibilities: [
        "Developed frontend components with React and integrated them with backend APIs.",
        "Built REST APIs using Node.js and Express to handle user data, NFTs, and transactions.",
        "Managed database schemas with MongoDB and Mongoose, including data validation and indexing.",
        "Implemented secure authentication using JWT and bcrypt.",
        "Assisted in deployment and testing, learning CI/CD and version control best practices."
      ]
    }
  ];

  return (
    <Container id="experience">
      <div className="header-section">
        
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          viewport={{ once: false }}
          className="main-title"
        >
          Professional Experience
        </motion.h2>
       
      </div>

      <div className="timeline-container">
        <div className="experiences-list">
          {experiences.map((exp, index) => (
            <ExperienceCard key={exp.id} exp={exp} index={index} />
          ))}
        </div>
      </div>

      
    </Container>
  );
};

const Container = styled.section`
  padding: 3rem 0;
  max-width: 1200px;
  margin: 0 auto;
  padding-left: 1rem;
  padding-right: 1rem;

  .header-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 4rem;
    text-align: center;
  }

  .career-badge {
    margin-bottom: 1rem;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background: #ecfdf5;
    color: #047857;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border: 1px solid #a7f3d0;

    .icon {
      width: 1rem;
      height: 1rem;
    }
  }

  .main-title {
    font-size: 2.5rem;
    font-weight: 900;
    color: #01a479;
    margin-bottom: 1rem;
    letter-spacing: -0.025em;
    text-transform: uppercase;

    @media (min-width: 768px) {
      font-size: 3rem;
    }
  }

  .subtitle {
    max-width: 32rem;
    color: #64748b;
    font-size: 1.125rem;
    line-height: 1.75;
  }

  .timeline-container {
    max-width: 64rem;
    margin: 0 auto;
  }

  .experiences-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .experience-item {
    margin-bottom: 2rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .experience-card {
    position: relative;
    background: white;
    border-radius: 1rem;
    padding: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    border: 1px solid #f1f5f9;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      transform: translateY(-4px);
    }

    @media (min-width: 768px) {
      padding: 2rem;
    }
  }

  .card-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;

    @media (min-width: 1024px) {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  }

  .title-section {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .position-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: color 0.3s ease;

    .icon {
      width: 1.25rem;
      height: 1.25rem;
      color: #059669;
    }

    @media (min-width: 768px) {
      font-size: 1.5rem;
    }
  }

  .company-info {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;
    color: #64748b;
    font-weight: 500;
    font-size: 0.95rem;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
    }
  }

  .company-name {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    color: #059669;

    .icon {
      width: 1rem;
      height: 1rem;
    }
  }

  .location, .duration {
    display: flex;
    align-items: center;
    gap: 0.375rem;

    .icon {
      width: 1rem;
      height: 1rem;
      color: #94a3b8;
    }
  }

  .separator {
    width: 0.375rem;
    height: 0.375rem;
    border-radius: 50%;
    background: #cbd5e1;

    @media (max-width: 768px) {
      display: none;
    }
  }

  .type-badge {
    display: flex;
    align-items: flex-start;
    align-self: flex-start;
  }

  .badge {
    padding: 0.375rem 1rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
    background: #ecfdf5;
    color: #047857;
    border: 1px solid #a7f3d0;
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;

    .icon {
      width: 1rem;
      height: 1rem;
      color: #059669;
    }

    h4 {
      font-size: 0.875rem;
      font-weight: 700;
      color: #0f172a;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      margin: 0;
    }
  }

  .overview-section {
    margin-bottom: 2rem;
  }

  .overview-text {
    color: #64748b;
    line-height: 1.75;
    font-size: 0.875rem;
    background: rgba(248, 250, 252, 0.5);
    padding: 1rem;
    border-radius: 0.75rem;
    border: 1px solid #f1f5f9;
    font-style: italic;
    margin: 0;

    @media (min-width: 768px) {
      font-size: 1rem;
    }
  }

  .tech-section {
    margin-bottom: 2rem;
  }

  .tech-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .tech-tag {
    padding: 0.25rem 0.75rem;
    background: white;
    color: #374151;
    font-size: 0.75rem;
    font-weight: 500;
    border-radius: 0.5rem;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
    cursor: default;

    &:hover {
      border-color: #10b981;
      color: #047857;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }

    @media (min-width: 768px) {
      font-size: 0.875rem;
    }
  }

  .responsibilities-section {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .responsibilities-list {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.75rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .responsibility-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    color: #64748b;
    font-size: 0.875rem;
    line-height: 1.75;
    transition: color 0.3s ease;

    &:hover {
      color: #0f172a;

      .chevron-icon {
        transform: translateX(0.25rem);
      }
    }

    @media (min-width: 768px) {
      font-size: 1rem;
    }
  }

  .chevron-icon {
    width: 1rem;
    height: 1rem;
    color: #059669;
    margin-top: 0.25rem;
    flex-shrink: 0;
    transition: transform 0.3s ease;
  }

  .cta-section {
    margin-top: 6rem;
    padding: 2rem;
    border-radius: 2rem;
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #064e3b 100%);
    color: white;
    position: relative;
    overflow: hidden;

    @media (min-width: 768px) {
      padding: 3rem;
    }
  }

  .cta-bg-effect-1 {
    position: absolute;
    top: 0;
    right: 0;
    width: 16rem;
    height: 16rem;
    background: rgba(5, 150, 105, 0.1);
    border-radius: 50%;
    filter: blur(80px);
    margin-right: -8rem;
    margin-top: -8rem;
  }

  .cta-bg-effect-2 {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 16rem;
    height: 16rem;
    background: rgba(5, 150, 105, 0.1);
    border-radius: 50%;
    filter: blur(80px);
    margin-left: -8rem;
    margin-bottom: -8rem;
  }

  .cta-content {
    position: relative;
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;

    @media (min-width: 768px) {
      flex-direction: row;
    }
  }

  .cta-text {
    text-align: center;

    @media (min-width: 768px) {
      text-align: left;
    }

    h3 {
      font-size: 1.5rem;
      font-weight: 700;
      margin: 0 0 1rem 0;

      @media (min-width: 768px) {
        font-size: 1.875rem;
      }
    }

    p {
      color: #cbd5e1;
      max-width: 28rem;
      margin: 0;
      line-height: 1.6;
    }
  }

  .cta-buttons {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    @media (min-width: 640px) {
      flex-direction: row;
    }
  }

  .primary-btn {
    padding: 1rem 2rem;
    background: #059669;
    color: white;
    border: none;
    border-radius: 1rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 10px 15px -3px rgba(5, 150, 105, 0.2);

    &:hover {
      background: #10b981;
    }
  }

  .secondary-btn {
    padding: 1rem 2rem;
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 1rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(4px);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

export default Experience;